'use strict';

import { MeshBasicMaterial } from 'three';
import {mergeVertices} from 'three/examples/jsm/utils/BufferGeometryUtils';

const int_pars_vertex = `
    varying vec3 vViewDirTangent;
`;

const int_void_vertex = `
    vec3 vNormal = normalMatrix * normal;
    vec3 vTangent = normalMatrix * tangent.xyz;
    vec3 vBitangent = normalize( cross( vNormal, vTangent ) * tangent.w );
    
    mat3 mTBN = transpose(mat3(vTangent, vBitangent, vNormal));
    vec4 mvPos = modelViewMatrix * vec4( position, 1.0 );
    vec3 viewDir = -mvPos.xyz;
    vViewDirTangent = mTBN * viewDir;
`;

const int_pars_fragment = `
    varying vec3 vViewDirTangent;
    uniform float midgroundDepth;
    uniform float roomDepth;
    uniform float brightness;
    uniform float contrast;
`;

const int_before_void_fragment = `
vec2 intersect(vec3 ro, vec3 rd, vec3 Axis, float d, float id1, float id2){
    float rd_weight = dot( rd, Axis );
    float ro_weight = dot( ro ,Axis );
    float pointer = ceil( ro_weight / d );

    if( rd_weight > 0.0 ) // look up
    {
        float h = pointer * d;
        float t = ( h - ro_weight ) / rd_weight;
        return vec2( t, id1 );
    }
    else // look down
    {
        float h = ( pointer - 1.0 ) * d;
        float t = ( h - ro_weight ) / rd_weight;
        return vec2( t, id2 );
    }
}
vec2 nearest( vec2 c1, vec2 c2, vec2 c3){
    if( c1.x < c2.x )
    {
        return c1.x<c3.x? c1:c3;
    }
    else
    {
        return c2.x<c3.x? c2:c3;
    }
}
vec4 renderColor( vec2 tID,  vec3 ro,  vec3 rd,  vec3 size, sampler2D map) {
    vec4 finalColor = vec4(0.0);
    float t = tID.x;
    float id = tID.y;

    vec3 pos = ro + t*rd;
    pos = pos / size;

    if (id>0.9 && id<1.1)
    {
        vec2 topUV = fract(vec2(pos.x,pos.z)) * vec2(1.0/3.0,1.0/3.0) + vec2(1.0/3.0,1.0/3.0 * 2.0);
        finalColor = texture2D( map, topUV);
    }
    else if (id>1.9 && id<2.1)
    {
        vec2 bottomUV = fract(vec2(pos.x,-pos.z)) * vec2(1.0/3.0,1.0/3.0) + vec2(1.0/3.0,0.0);
        finalColor = texture2D( map, bottomUV );
    }
    else if (id>2.9 && id<3.1)
    {
        vec2 rightUV = fract(vec2(pos.z,pos.y)) * vec2(1.0/3.0,1.0/3.0) + vec2(1.0/3.0 * 2.0,1.0/3.0);
        finalColor = texture2D( map, rightUV );
    }
    else if (id>3.9 && id<4.1)
    {
        vec2 leftUV = fract(vec2(-pos.z,pos.y)) * vec2(1.0/3.0,1.0/3.0) + vec2(0.0,1.0/3.0);
        finalColor = texture2D( map, leftUV );
    }
    else if (id>4.9 && id<5.1)
    {
        vec2 frontUV = fract(pos.xy) * vec2(1.0/3.0,1.0/3.0);
        finalColor = texture2D( map, frontUV);
    }
    else if (id>5.9 && id<6.1)
    {
        vec2 backUV = fract(pos.xy) * vec2(1.0/3.0,1.0/3.0) + vec2(1.0/3.0,1.0/3.0);
        finalColor = texture2D( map, backUV );
    } else if (id >6.9 && id <8.1) {
        vec2 midUV = fract(pos.xy) * vec2(1.0/3.0,1.0/3.0) + vec2(0.0,1.0/3.0 * 2.0);
        finalColor = texture2D( map, midUV );
    }

    return finalColor;
}
`;

const int_void_fragment = `
    #ifdef USE_MAP
	    vec3 ro = vec3( vMapUv, 1.0 );
        vec3 rd = normalize(ro - vViewDirTangent);
        rd.z /= roomDepth;
    
        vec3 size = vec3(1.0);
        float midgroundDepthMult = clamp(midgroundDepth,0.01,size.z-0.01);
    
        vec3 up = vec3(0.0, 1.0, 0.0);
        vec3 right = vec3(1.0, 0.0, 0.0);
        vec3 back = vec3(0.0, 0.0, 1.0);
        vec3 front = vec3(0.0, 0.0, -1.0);
        vec3 mid = vec3(0.0,0.0,1.0);
    
        // interset
        vec2 tID1 = intersect(ro,rd,up,size.y, 1.0, 2.0);
        vec2 tID2 = intersect(ro,rd,right,size.x, 3.0, 4.0);
        vec2 tBack = intersect(ro,rd,back,size.z, 5.0, 6.0);
        vec2 tFront = intersect(ro,rd,front,size.z, 5.0, 6.0);
        vec2 tMid = intersect(ro - midgroundDepthMult,rd,mid,size.z, 7.0, 8.0);
    
        vec2 tIDCube = nearest(tID1,tID2,tBack);
        vec2 tIDFront = nearest(tID1,tID2,tFront);
        vec2 tIDMid = nearest(tID1,tID2,tMid);
    
        // render
        vec4 sampledDiffuseColor = renderColor(tIDCube,ro,rd,size,map);
        
        #if MIDGROUND == 1
       
            vec4 midColor = renderColor(tIDMid,ro,rd,size,map);
            sampledDiffuseColor = mix(sampledDiffuseColor,midColor,midColor.a);
       
        #endif
        
        #if CURTAINS == 1
       
            vec4 frontColor = renderColor(tIDFront,ro,rd,size,map);
            sampledDiffuseColor = mix(sampledDiffuseColor,frontColor,frontColor.a);
        
        #endif
	    
	    sampledDiffuseColor.rgb /= sampledDiffuseColor.a;
        sampledDiffuseColor.rgb = ((sampledDiffuseColor.rgb - 0.5f) * contrast) + 0.5f;
	    sampledDiffuseColor.rgb += brightness;
	    sampledDiffuseColor.rgb *= sampledDiffuseColor.a;
	    
	    diffuseColor *= sampledDiffuseColor;

    #endif
`;


export class InteriorMaterial extends MeshBasicMaterial{
	#shader;
	
	constructor (parameters) {
		super(parameters);
		
		this.midgroundDepth = 0.5;
		this.roomDepth = 1;
		this.contrast = 1;
		this.brightness = 0;
		this.enableMidground = true;
		this.enableCurtains = false;
		this.defines = {
			INTERIOR_MATERIAL:'',
			USE_TANGENT: '',
			MIDGROUND: 1,
			CURTAINS: 0
		};
		
		this.customPrograms = [this.upgradeShader.bind(this)];
	}
	
	upgradeShader(shader) {
		shader.defines.MIDGROUND = Number(this.enableMidground);
		shader.defines.CURTAINS = Number(this.enableCurtains);
		
		shader.uniforms.midgroundDepth = {
			value: this.midgroundDepth
		};
		
		shader.uniforms.roomDepth = {
			value: this.roomDepth
		};
		
		shader.uniforms.brightness = {
			value: this.brightness
		};
		
		shader.uniforms.contrast = {
			value: this.contrast
		};
		
		shader.vertexShader = shader.vertexShader.replace('#include <clipping_planes_pars_vertex>',[
			'#include <clipping_planes_pars_vertex>',
			int_pars_vertex
		].join('\n'));
		
		shader.vertexShader = shader.vertexShader.replace('#include <worldpos_vertex>',[
			int_void_vertex,
			'#include <worldpos_vertex>'
		].join('\n'));
		
		shader.fragmentShader = int_pars_fragment + shader.fragmentShader;
		
		shader.fragmentShader = shader.fragmentShader.replace('#include <clipping_planes_pars_fragment>',[
			'#include <clipping_planes_pars_fragment>',
			int_before_void_fragment
		].join('\n'));
		
		shader.fragmentShader = shader.fragmentShader.replace('#include <map_fragment>',int_void_fragment);
		
		this.#shader = shader;
	}
	
	static prepareMesh(mesh) {
		if (mesh.geometry.index === null) {
			mesh.geometry = mergeVertices(mesh.geometry);
		}
		
		if (!mesh.geometry.hasAttribute('tangent')) {
			mesh.geometry.computeTangents();
		}
	}
	
}
